"use client";

import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import type { VehicleType } from "~/lib/constants/vehicle";
import type { RetrievedPlace } from "~/lib/mapbox/mapbox";
import {
  applySearchToParams,
  isSameDestination,
  isSameSearch,
} from "~/lib/search-url";
import { isWithinServiceArea } from "~/lib/service-area";
import {
  floorTo15,
  resolveSearchWindow,
  type SearchWindow,
} from "~/lib/utils/datetime";
import { useSearchDraftStore } from "~/store/searchDraftStore";
import { useDestinationTimeZone } from "./useDestinationTimeZone";

// ---------------------------------------------------------------------------
// Slot clock
// ---------------------------------------------------------------------------

// One clock for the whole app, so the four consumers on the search page can't
// disagree about which slot is current. No timer: a page left open keeps the window
// it searched and reports it via `staleWindow` instead of moving under the reader.
let slot = floorTo15(new Date());
// Apart from `slot` because a render can advance the cache before a refresh runs,
// and comparing the two would then skip the notification.
let notifiedSlotTime = slot.getTime();
const slotListeners = new Set<() => void>();

/**
 * The current slot, cached. Stable identity within a slot, which is what
 * `useSyncExternalStore` requires and what makes this safe as a dependency.
 */
function readSlot(): Date {
  const next = floorTo15(new Date());
  if (next.getTime() !== slot.getTime()) slot = next;
  return slot;
}

/** Call in the handler that opens a picker, not an effect, so the first paint is right. */
export function refreshNowSlot(): void {
  const next = readSlot();
  if (next.getTime() === notifiedSlotTime) return;
  notifiedSlotTime = next.getTime();
  for (const listener of slotListeners) listener();
}

/**
 * The clock as an external store. Exported so its subscribe/read contract can be
 * exercised directly — `useNowSlot` itself needs a renderer, and the parts worth
 * covering are the notification guard and the listener lifecycle, not the hook.
 */
export const slotClock = {
  subscribe: subscribeToSlot,
  read: readSlot,
};

function onTabForeground() {
  if (document.visibilityState === "visible") refreshNowSlot();
}

function subscribeToSlot(listener: () => void): () => void {
  slotListeners.add(listener);
  if (slotListeners.size === 1) {
    document.addEventListener("visibilitychange", onTabForeground);
    window.addEventListener("focus", onTabForeground);
  }
  return () => {
    slotListeners.delete(listener);
    if (slotListeners.size > 0) return;
    document.removeEventListener("visibilitychange", onTabForeground);
    window.removeEventListener("focus", onTabForeground);
  };
}

/**
 * The current 15-minute slot, re-read when the tab returns to the foreground and on
 * `refreshNowSlot`. Between those it holds still on purpose.
 *
 * `readSlot` serves the server snapshot too, which keeps the module-level cache from
 * going stale between requests but does not make the first paint agree: React calls
 * the server snapshot on the *client* during hydration, so it reads the client clock
 * while the SSR HTML holds the server's. Anything rendered from `now` mismatches when
 * the two zones differ.
 */
export function useNowSlot(): Date {
  return useSyncExternalStore(
    slotClock.subscribe,
    slotClock.read,
    slotClock.read,
  );
}

/**
 * Owns the start/end pair for one search entry point.
 *
 * `timeZone` is the destination's, and moves as the driver picks a place. It is
 * what the past-start floor is measured against, so the window this returns
 * can't disagree with the slots `DateTimeFields` offers for the same place —
 * see `resolveSearchWindow`. A zone change re-resolves for the same reason a
 * slot change does: the start that was still ahead in the old city may already
 * have passed in the new one.
 */
export function useSearchWindow(
  initial: Partial<SearchWindow>,
  timeZone?: string | null,
) {
  const now = useNowSlot();
  const [range, setRange] = useState(() =>
    resolveSearchWindow(initial, now, timeZone),
  );
  const [reconciledSlot, setReconciledSlot] = useState(now);
  const [reconciledZone, setReconciledZone] = useState(timeZone);

  // Reconciled during render, not in an effect: the pickers must never be handed
  // a window from a slot that has already passed, not even for one paint.
  if (reconciledSlot !== now || reconciledZone !== timeZone) {
    setReconciledSlot(now);
    setReconciledZone(timeZone);
    setRange((prev) => resolveSearchWindow(prev, now, timeZone));
  }

  // No `now` argument: resolveSearchWindow defaults to the live clock, which is
  // never staler than a captured slot and matches how the draft store's setters
  // resolve. It floors internally, so the result is identical when the two agree.
  const setStartsAt = useCallback(
    (starts_at: string) =>
      setRange((prev) =>
        resolveSearchWindow({ ...prev, starts_at }, undefined, timeZone),
      ),
    [timeZone],
  );

  const setEndsAt = useCallback(
    (ends_at: string) =>
      setRange((prev) =>
        resolveSearchWindow({ ...prev, ends_at }, undefined, timeZone),
      ),
    [timeZone],
  );

  return { ...range, now, setStartsAt, setEndsAt };
}

/** `Number` maps both null and "" to 0, so presence is checked before parsing. */
function coordFromParam(value: string | null): number | null {
  if (value === null || value.trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function placeFromParams(params: URLSearchParams): RetrievedPlace | null {
  const lat = coordFromParam(params.get("lat"));
  const lng = coordFromParam(params.get("lng"));
  // An unparseable coordinate is no place at all — treating it as one would let
  // the submit gate pass and write `lat=NaN` straight back into the next URL.
  if (lat === null || lng === null) return null;
  return {
    label: params.get("q") ?? "",
    name: params.get("dest_name"),
    lat,
    lng,
    city: params.get("city") ?? "",
    state: params.get("state") ?? "",
  };
}

function draftFromParams(paramsKey: string) {
  const params = new URLSearchParams(paramsKey);
  return {
    q: params.get("q") ?? "",
    place: placeFromParams(params),
    starts_at: params.get("starts_at") ?? "",
    ends_at: params.get("ends_at") ?? "",
    vehicleSize: (params.get("vehicle_size") as VehicleType | null) ?? null,
  };
}

/**
 * The shared `/search` draft, kept seeded from the URL and current for the clock.
 *
 * Safe to call from all three editing surfaces at once: `hydrate` is keyed on the
 * param string and `reconcile` is idempotent, so one change yields one update.
 */
export function useSearchDraft() {
  const rawParams = useSearchParams();
  const now = useNowSlot();
  const draft = useSearchDraftStore();
  const { hydrate, reconcile } = draft;
  // Also what starts the boundary table's fetch for this page: the three editors
  // all go through this hook, so mounting any of them warms the table well before
  // a destination resolves.
  const destTimeZone = useDestinationTimeZone(
    draft.place?.lat,
    draft.place?.lng,
  );

  // The draft belongs to the editors, not to the session. Clearing the seed key
  // when the last one unmounts is what makes an abandoned edit die with the page
  // instead of reappearing, committed-looking, on the way back.
  useEffect(() => {
    const { retain, release } = useSearchDraftStore.getState();
    retain();
    return release;
  }, []);

  const paramsKey = rawParams.toString();
  useEffect(() => {
    hydrate(paramsKey, draftFromParams(paramsKey));
  }, [paramsKey, hydrate]);

  // The boundary table is a fetched chunk, so it lands after the seed above has
  // already run — and a seed with no zone floors the window on the *viewer's*
  // clock, the thing this whole path exists to avoid. Re-resolving once the
  // destination's zone is actually known restores what an eagerly-loaded table
  // would have produced. Cheap to run: `reconcile` hands back the same object
  // when nothing moved, so a window that was already right re-renders nothing.
  useEffect(() => {
    if (destTimeZone) reconcile(new Date());
  }, [destTimeZone, reconcile]);

  /**
   * Snaps the window up to the current slot. Called on date-panel open, not on every
   * clock move: a closed pill keeps the searched times, so it agrees with the results.
   */
  const reconcileWindow = useCallback(() => reconcile(new Date()), [reconcile]);

  const nextParams = applySearchToParams(
    {
      q: draft.q,
      place: draft.place,
      starts_at: draft.starts_at,
      ends_at: draft.ends_at,
      vehicleSize: draft.vehicleSize,
    },
    new URLSearchParams(paramsKey),
  );
  const searchUrl = nextParams ? `/search?${nextParams.toString()}` : null;

  /**
   * Whether the draft would actually change the URL. Lets a submit control show
   * that there is nothing to run, rather than sitting there live and doing
   * nothing — closing already commits, so by the time the user reaches for the
   * button the search it would run is usually the one already on screen.
   */
  const hasPendingChanges =
    !!nextParams && !isSameSearch(nextParams, new URLSearchParams(paramsKey));

  /**
   * Whether the drafted destination lies outside every EZSpot market.
   *
   * On the coordinate rather than the city string: every destination has one,
   * and the API enforces the same polygons. Derived here so the two editors
   * sharing this draft can't disagree, and so `commitSearch` below can refuse
   * on it — closing an editor commits, so a gate living only in the submit
   * buttons would let a click outside run the search anyway.
   */
  const outOfArea =
    draft.place != null &&
    !isWithinServiceArea(draft.place.lat, draft.place.lng);

  /** Restores the committed destination when an edit is abandoned mid-typing. */
  const revertToCommittedLocation = useCallback(() => {
    const params = new URLSearchParams(paramsKey);
    useSearchDraftStore.getState().revertLocation({
      q: params.get("q") ?? "",
      place: placeFromParams(params),
    });
  }, [paramsKey]);

  /**
   * Discards the draft back to the URL. For an editor that reopens on a URL it
   * has already seeded — `hydrate` no-ops there, so without this an edit that
   * was closed rather than submitted would still be sitting there next time.
   */
  const reseedFromUrl = useCallback(() => {
    useSearchDraftStore
      .getState()
      .reseed(paramsKey, draftFromParams(paramsKey));
  }, [paramsKey]);

  /**
   * Runs the draft as it stands right now, if it has moved and is runnable.
   *
   * Reads the store imperatively rather than closing over `draft`: callers commit
   * in the same tick they change a field — after an awaited `retrievePlace`, the
   * render's `searchUrl` still describes the search without that place. Zustand's
   * `set` is synchronous, so `getState()` here sees whatever was just written.
   *
   * A resolved place is required for the same reason the pill's submit gate wants
   * one: `/search` needs coordinates, so committing text alone would land the user
   * back on the empty state asking for the address they just typed. A place
   * outside the service area is refused on the same grounds — see `outOfArea`.
   *
   * `pushState`, not `router.push`. We are already on `/search` and its server
   * component never reads `searchParams` — it only wraps `FlagGate` — so a router
   * navigation spends a whole RSC round trip, identity lookup and flag evaluation
   * before the client sees the new params and can start the search it was asked
   * for. Next patches the History API and re-dispatches the URL into the router, so
   * `useSearchParams` picks this up — as a transition, not synchronously, but with
   * no network hop in the way. Gating is unaffected: `driverProcedure` enforces the
   * flag on every request of its own accord.
   */
  const commitSearch = useCallback(() => {
    const draftNow = useSearchDraftStore.getState();
    if (!draftNow.place) return;
    if (!isWithinServiceArea(draftNow.place.lat, draftNow.place.lng)) return;
    // Read live rather than through `useSearchParams`, which lags a `pushState` by
    // the router's transition. Every field this writes comes from the draft, so a
    // stale base would still produce the right URL — but it would compare against
    // the wrong one below and push a duplicate entry.
    const current = new URLSearchParams(window.location.search);
    const next = applySearchToParams(
      {
        q: draftNow.q,
        place: draftNow.place,
        starts_at: draftNow.starts_at,
        ends_at: draftNow.ends_at,
        vehicleSize: draftNow.vehicleSize,
      },
      current,
    );
    if (!next || isSameSearch(next, current)) return;
    // Only a new destination earns a history entry. Changing the time or the vehicle
    // size refines the place already on screen, so it overwrites that entry — Back
    // then steps between places searched rather than every adjustment made to one.
    const url = `/search?${next.toString()}`;
    if (isSameDestination(next, current)) {
      window.history.replaceState(null, "", url);
    } else {
      window.history.pushState(null, "", url);
    }
  }, []);

  return {
    ...draft,
    now,
    // The destination's clock, for the editors' time fields. Derived here rather
    // than in each of the three so they can't disagree, and matching what the
    // store itself resolves the window against.
    timeZone: destTimeZone,
    searchUrl,
    hasPendingChanges,
    outOfArea,
    commitSearch,
    revertToCommittedLocation,
    reseedFromUrl,
    reconcileWindow,
  };
}
